import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-1 gap-2 overflow-hidden" }
const _hoisted_2 = { class: "flex flex-col gap-0.5 p-2 min-w-[160px] h-full overflow-y-scroll bg-white border border-gray-200 shadow sm:rounded-lg" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col flex-1"
}
const _hoisted_4 = { class: "flex flex-col p-2 min-w-[380px] flex-1 overflow-y-scroll bg-white border border-gray-200 shadow sm:rounded-lg" }
const _hoisted_5 = { class: "pl-1 pr-2 py-2 rounded-md cursor-pointer w-full flex justify-between items-center gap-3" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = {
  key: 0,
  type: "checkbox",
  class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
  checked: true
}
const _hoisted_8 = { class: "pl-3 bg-white" }
const _hoisted_9 = { class: "text-gray-400 ml-1" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "relative py-1 pl-4 rounded-md cursor-pointer w-full group/select-only" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["checked"]
const _hoisted_14 = { class: "ml-3" }
const _hoisted_15 = { class: "text-gray-400 ml-1" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 1,
  class: "p-2 min-w-[380px] bg-white border flex flex-col border-gray-200 shadow sm:rounded-lg"
}
const _hoisted_18 = ["placeholder"]
const _hoisted_19 = { class: "overflow-y-scroll flex-1" }
const _hoisted_20 = { class: "flex flex-col gap-0.5 relative" }
const _hoisted_21 = { class: "font-bold pl-1 sticky top-0 bg-white" }
const _hoisted_22 = {
  key: 0,
  class: "pl-1 pr-2 py-2 rounded-md cursor-pointer w-full flex justify-between items-center gap-3"
}
const _hoisted_23 = ["onClick"]
const _hoisted_24 = {
  key: 0,
  type: "checkbox",
  class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
  checked: true
}
const _hoisted_25 = { class: "ml-3" }
const _hoisted_26 = { class: "text-gray-400 ml-1" }
const _hoisted_27 = { class: "flex flex-col gap-0.5 relative" }
const _hoisted_28 = { class: "font-bold pl-1 mt-2 sticky top-0 bg-white" }
const _hoisted_29 = ["onClick"]
const _hoisted_30 = ["checked"]

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import { processClasses } from "@oculai/planner";
import { PropType, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import MainButton from "@/components/UI/MainButton.vue";
import { ProcessReport } from "@/types/Process";

type ProcessGroup = {
  _id: string;
  name: string;
  key: string;
  items: number[];
};

type ProcessClassGroup = {
  name: string;
  items: number[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ProcessPicker',
  props: {
  presets: {
    type: Array as PropType<ProcessGroup[]>,
    required: true,
  },
  selected: {
    type: Object as PropType<ProcessReport["processes"]>,
    required: true,
  },
},
  emits: ["onSave"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

const emits = __emit;

const groups = ref<ProcessGroup[]>([]);
const searchValue = ref("");
const openedProcessClassesGroups = ref(new Set<string>());
const selectedProcesses = ref(new Set<number>());
const selectedPresets = ref<Record<string, Set<number>>>({});
const mode = ref<"component" | "single_process">("component");

watch(
  () => [props.presets, props.selected],
  (value) => {
    const [presets, selected] = value as [ProcessGroup[], ProcessReport["processes"]];

    selectedProcesses.value = new Set(selected.single_processes);
    selectedPresets.value = presets.reduce((acc, preset) => {
      const selectedPresetProcesses =
        selected.components.find((component) => component.id === preset._id)?.processes || [];

      acc[preset.name] = new Set(selectedPresetProcesses);

      return acc;
    }, {} as Record<string, Set<number>>);

    groups.value = [...presets];
    mode.value = selected.mode;
  },
  { immediate: true },
);

const processClassGroups = computed(() => {
  const groups = processClasses.reduce((acc, process) => {
    if (!props.presets.some((preset) => preset.items.includes(process.encodedLabel))) {
      return acc;
    }

    const group = acc.find((group) => group.name === process.processElementLabel);

    if (group) {
      group.items.push(process.encodedLabel);
    } else {
      acc.push({
        name: process.processElementLabel,
        items: [process.encodedLabel],
      });
    }

    return acc;
  }, [] as ProcessClassGroup[]);

  return groups;
});

const countSelected = (
  group: ProcessGroup | ProcessClassGroup,
  processClassGroup?: ProcessClassGroup,
) => {
  if (processClassGroup) {
    const selectedFromGroup = group.items.filter((process) =>
      processClassGroup?.items.includes(process),
    );

    const selected = selectedFromGroup.filter((process) => selectedProcesses.value.has(process));

    return [selected.length, selectedFromGroup.length];
  }

  const selectedFromGroup = group.items.filter((process) => selectedProcesses.value.has(process));

  return [selectedFromGroup.length, group.items.length];
};

const countSelectedInPreset = (group: ProcessGroup, processClassGroup?: ProcessClassGroup) => {
  if (processClassGroup) {
    const selectedFromGroup = group.items.filter((process) =>
      processClassGroup?.items.includes(process),
    );

    const selected = selectedFromGroup.filter((process) =>
      selectedPresets.value[group.name].has(process),
    );

    return [selected.length, selectedFromGroup.length];
  }

  const selectedFromGroup = group.items.filter((process) =>
    selectedPresets.value[group.name].has(process),
  );

  return [selectedFromGroup.length, group.items.length];
};

const handleProcessGroupToggle = (group: string) => {
  if (openedProcessClassesGroups.value.has(group)) {
    openedProcessClassesGroups.value.delete(group);
  } else {
    openedProcessClassesGroups.value.add(group);
  }
};

const handleUpdatePresetGroup = (
  group: ProcessGroup,
  item?: ProcessClassGroup,
  selectOnly = false,
) => {
  const preset = groups.value.find((preset) => preset.name === group.name);
  const selectedPreset = selectedPresets.value[group.name];

  if (!preset || !selectedPreset) {
    return;
  }

  let itemsToUpdate = preset.items;

  if (item) {
    itemsToUpdate = item.items.filter((process) => preset.items.includes(process));
  }

  if (selectOnly) {
    const filteredPresets = Object.fromEntries(
      Object.keys(selectedPresets.value).map((key) => [key, new Set<number>()]),
    );

    selectedPresets.value = {
      ...filteredPresets,
      [group.name]: new Set(itemsToUpdate),
    };
    return;
  }

  const isAdditionOperation = itemsToUpdate.some((process) => !selectedPreset.has(process));

  if (isAdditionOperation) {
    itemsToUpdate.forEach((process) => {
      selectedPreset.add(process);
    });
  } else {
    itemsToUpdate.forEach((process) => {
      selectedPreset.delete(process);
    });
  }
};

const handleUpdateGroup = (
  item: number | ProcessClassGroup,
  group?: ProcessGroup,
  selectOnly = false,
) => {
  if (typeof item === "number") {
    if (selectedProcesses.value.has(item)) {
      selectedProcesses.value.delete(item);
    } else {
      selectedProcesses.value.add(item);
    }

    return;
  }

  let itemsToUpdate = item.items;

  if (group) {
    itemsToUpdate = group.items.filter((process) => item.items.includes(process));
  }

  if (selectOnly) {
    selectedProcesses.value = new Set(itemsToUpdate);
    return;
  }

  const isAdditionOperation = itemsToUpdate.some(
    (process) => !selectedProcesses.value.has(process),
  );

  if (isAdditionOperation) {
    itemsToUpdate.forEach((process) => {
      selectedProcesses.value.add(process);
    });
  } else {
    itemsToUpdate.forEach((process) => {
      selectedProcesses.value.delete(process);
    });
  }
};

const handleClear = () => {
  selectedProcesses.value = new Set();
  selectedPresets.value = groups.value.reduce((acc, group) => {
    acc[group.name] = new Set();

    return acc;
  }, {} as Record<string, Set<number>>);
};

const handleSwitchMode = (modeToSwitch: typeof mode.value) => {
  if (modeToSwitch === mode.value) {
    return;
  }

  mode.value = modeToSwitch;
};

const handleSave = (close: CallableFunction) => {
  const payload = {
    mode: mode.value,
  } as Record<string, unknown>;

  if (mode.value === "single_process") {
    payload.single_processes = Array.from(selectedProcesses.value);
    payload.components = [];
  } else {
    payload.components = Object.entries(selectedPresets.value)
      .filter(([, value]) => value.size > 0)
      .map(([key, value]) => {
        const id = groups.value.find((group) => group.name === key)?._id;

        return { id: id, processes: Array.from(value) };
      });
    payload.single_processes = [];
  }

  emits("onSave", payload);

  searchValue.value = "";

  close();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), { class: "flex items-start" }, {
    default: _withCtx(() => [
      _createVNode(_unref(PopoverButton), { class: "outline-none" }, {
        default: _withCtx(() => [
          _renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }),
      _createVNode(_unref(PopoverPanel), {
        class: "flex flex-col gap-2 absolute left-0 top-[calc(100%+5px)] z-10 bg-gray-50 border border-gray-200 shadow rounded-lg p-2 h-96 text-sm",
        style: {"width":"max-content"}
      }, {
        default: _withCtx(({ close }) => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("p", {
                class: _normalizeClass([
              'py-2 px-3 rounded-md cursor-pointer w-full',
              mode.value === 'component' ? 'bg-yellow-300' : 'hover:bg-gray-100',
            ]),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (handleSwitchMode('component')))
              }, _toDisplayString(_unref(t)("analytics.reports.preset_mode")), 3),
              _createElementVNode("p", {
                class: _normalizeClass([
              'py-2 px-3 rounded-md cursor-pointer w-full',
              mode.value === 'single_process' ? 'bg-yellow-300' : 'hover:bg-gray-100',
            ]),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (handleSwitchMode('single_process')))
              }, _toDisplayString(_unref(t)("analytics.reports.process_mode")), 3)
            ]),
            (mode.value === 'component')
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                      return (_openBlock(), _createElementBlock(_Fragment, {
                        key: group.name
                      }, [
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("div", {
                            class: "flex items-center",
                            onClick: ($event: any) => (handleUpdatePresetGroup(group))
                          }, [
                            (countSelectedInPreset(group).every((count, _, arr) => count === arr[0]))
                              ? (_openBlock(), _createElementBlock("input", _hoisted_7))
                              : (_openBlock(), _createElementBlock("div", {
                                  key: 1,
                                  class: _normalizeClass([
                      'h-4 w-4 rounded  flex items-center justify-center ring-inset select-none',
                      countSelectedInPreset(group)[0] === 0
                        ? 'bg-white ring-1 ring-gray-300 ring-inset'
                        : 'bg-yellow-600 text-white',
                    ])
                                }, _toDisplayString(countSelectedInPreset(group)[0] === 0 ? "" : "-"), 3)),
                            _createElementVNode("span", _hoisted_8, _toDisplayString(group.name), 1),
                            _createElementVNode("span", _hoisted_9, " (" + _toDisplayString(group.items.length) + ") ", 1)
                          ], 8, _hoisted_6),
                          _createElementVNode("div", {
                            class: "flex-1 flex justify-end",
                            onClick: ($event: any) => (handleProcessGroupToggle(group.name))
                          }, [
                            _createVNode(_unref(ChevronDownIcon), {
                              class: _normalizeClass([
                      'h-4 w-4 transform transition-transform',
                      openedProcessClassesGroups.value.has(group.name) ? 'rotate-0' : 'rotate-90',
                    ])
                            }, null, 8, ["class"])
                          ], 8, _hoisted_10)
                        ]),
                        _createElementVNode("div", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(processClassGroups.value, (processClassGroup) => {
                            return (_openBlock(), _createElementBlock(_Fragment, {
                              key: processClassGroup.name
                            }, [
                              (processClassGroup.items.some((process) => group.items.includes(process)))
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(!openedProcessClassesGroups.value.has(group.name) && 'overflow-hidden h-0')
                                  }, [
                                    _createElementVNode("div", _hoisted_11, [
                                      _createElementVNode("div", {
                                        class: "flex items-center",
                                        onClick: ($event: any) => (handleUpdatePresetGroup(group, processClassGroup))
                                      }, [
                                        _createElementVNode("input", {
                                          type: "checkbox",
                                          class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
                                          checked: 
                            countSelectedInPreset(group, processClassGroup).every(
                              (count, _, arr) => count === arr[0],
                            )
                          
                                        }, null, 8, _hoisted_13),
                                        _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(t)(
                              `report.sections.processes.process_type_entries.${processClassGroup.name}`,
                            )), 1),
                                        _createElementVNode("span", _hoisted_15, " (" + _toDisplayString(countSelectedInPreset(group, processClassGroup)[1]) + ") ", 1)
                                      ], 8, _hoisted_12),
                                      _createElementVNode("p", {
                                        class: "group-hover/select-only:block hidden hover:bg-gray-200 absolute right-0 bg-gray-100 z-10 px-1 py-0.5 rounded-md top-1/2 transform -translate-y-1/2 text-xs",
                                        onClick: ($event: any) => (handleUpdatePresetGroup(group, processClassGroup, true))
                                      }, _toDisplayString(_unref(t)("analytics.reports.only")), 9, _hoisted_16)
                                    ])
                                  ], 2))
                                : _createCommentVNode("", true)
                            ], 64))
                          }), 128))
                        ])
                      ], 64))
                    }), 128))
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  _withDirectives(_createElementVNode("input", {
                    type: "text",
                    class: "w-full px-3 py-1 text-sm mb-3 mt-1 rounded-md border border-gray-300 focus:ring-yellow-500 focus:border-yellow-500",
                    placeholder: _unref(t)('buttons.search'),
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchValue).value = $event))
                  }, null, 8, _hoisted_18), [
                    [_vModelText, searchValue.value]
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("div", _hoisted_20, [
                      _createElementVNode("p", _hoisted_21, _toDisplayString(_unref(t)("analytics.processes.process_group")), 1),
                      (selectedProcesses.value.size > 0)
                        ? (_openBlock(), _createElementBlock("p", {
                            key: 0,
                            class: "absolute top-0 right-0 text-gray-500 text-xs hover:underline cursor-pointer",
                            onClick: handleClear
                          }, _toDisplayString(_unref(t)("buttons.clear")), 1))
                        : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(processClassGroups.value, (processClassGroup) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: processClassGroup.name
                        }, [
                          (
                    _unref(t)(`report.sections.processes.process_type_entries.${processClassGroup.name}`)
                      .toLowerCase()
                      .includes(searchValue.value?.toLowerCase() || '') ||
                    processClassGroup.items.some((process) =>
                      _unref(t)(`report.sections.processes.process_type_entries.${process}`)
                        .toLowerCase()
                        .includes(searchValue.value?.toLowerCase() || ''),
                    )
                  )
                            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                _createElementVNode("div", {
                                  class: "flex items-center justify-between",
                                  onClick: ($event: any) => (handleUpdateGroup(processClassGroup))
                                }, [
                                  (
                        countSelected(processClassGroup).every((count, _, arr) => count === arr[0])
                      )
                                    ? (_openBlock(), _createElementBlock("input", _hoisted_24))
                                    : (_openBlock(), _createElementBlock("div", {
                                        key: 1,
                                        class: _normalizeClass([
                        'h-4 w-4 rounded  flex items-center justify-center ring-inset select-none',
                        countSelected(processClassGroup)[0] === 0
                          ? 'bg-white ring-1 ring-gray-300 ring-inset'
                          : 'bg-yellow-600 text-white',
                      ])
                                      }, _toDisplayString(countSelected(processClassGroup)[0] === 0 ? "" : "-"), 3)),
                                  _createElementVNode("span", _hoisted_25, _toDisplayString(_unref(t)(
                          `report.sections.processes.process_type_entries.${processClassGroup.name}`,
                        )), 1),
                                  _createElementVNode("span", _hoisted_26, " (" + _toDisplayString(countSelected(processClassGroup).join("/")) + ") ", 1)
                                ], 8, _hoisted_23)
                              ]))
                            : _createCommentVNode("", true)
                        ], 64))
                      }), 128))
                    ]),
                    _createElementVNode("div", _hoisted_27, [
                      _createElementVNode("p", _hoisted_28, _toDisplayString(_unref(t)("analytics.processes.processes")), 1),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(processClasses), (process) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: process.encodedLabel
                        }, [
                          (
                    _unref(t)(
                      `report.sections.processes.process_type_entries.${process.processElementLabel}`,
                    )
                      .toLowerCase()
                      .includes(searchValue.value?.toLowerCase() || '') ||
                    _unref(t)(`report.sections.processes.process_type_entries.${process.encodedLabel}`)
                      .toLowerCase()
                      .includes(searchValue.value?.toLowerCase() || '')
                  )
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "py-2 px-3 rounded-md cursor-pointer w-full flex items-center gap-3 hover:bg-yellow-200",
                                onClick: ($event: any) => (handleUpdateGroup(process.encodedLabel))
                              }, [
                                _createElementVNode("input", {
                                  type: "checkbox",
                                  class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
                                  checked: selectedProcesses.value.has(process.encodedLabel)
                                }, null, 8, _hoisted_30),
                                _createElementVNode("span", null, _toDisplayString(_unref(t)(`report.sections.processes.process_type_entries.${process.encodedLabel}`)), 1)
                              ], 8, _hoisted_29))
                            : _createCommentVNode("", true)
                        ], 64))
                      }), 128))
                    ])
                  ])
                ]))
          ]),
          _createVNode(MainButton, {
            label: _unref(t)('buttons.apply'),
            class: "sm:mr-0",
            type: "button",
            color: "yellow",
            onClick: ($event: any) => (handleSave(close))
          }, null, 8, ["label", "onClick"])
        ]),
        _: 1
      })
    ]),
    _: 3
  }))
}
}

})